import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Textarea } from '../../components/ui/form';
import { ApiError } from '../../lib/api';
import { MAX_BODY, MAX_IMAGES, posts, type Post, type Visibility } from './api';

/**
 * Writing an update, or editing one.
 *
 * ## The audience is a choice made before posting, not after
 *
 * Both options are on screen while the text is being written, with `Public`
 * selected. A control that defaults to the wider audience and is easy to miss is
 * the one that turns a followers-only thought into a public one, so it sits
 * under the box rather than behind a menu - and it says who will see it in
 * words, not as an icon somebody has to learn.
 *
 * ## The counter appears when it starts to matter
 *
 * Not at zero characters. A number that is present from the first keystroke
 * reads as a limit to write up to; one that arrives near the end reads as a
 * warning, which is what it is.
 */
export function PostComposer({
  /** Present when editing rather than writing. */
  editing,
  onDone,
  username,
}: {
  editing?: Post;
  onDone?: () => void;
  /** Whose profile the list belongs to, so the right query is refreshed. */
  username: string;
}) {
  const [body, setBody] = useState(editing?.body ?? '');
  const [visibility, setVisibility] = useState<Visibility>(editing?.visibility ?? 'public');
  /**
   * Pictures already uploaded, waiting to be attached.
   *
   * Uploaded when picked rather than when posted, so pressing Post is instant
   * and the wait happens while somebody is still writing. An abandoned draft
   * leaves objects in the scratch prefix, which a 24-hour lifecycle rule
   * collects - see `ImageUploads`.
   */
  const [images, setImages] = useState<{ key: string; preview: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const picker = useRef<HTMLInputElement | null>(null);
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: () =>
      editing
        ? posts.update(editing.id, body.trim(), visibility)
        : posts.create(
            body.trim(),
            visibility,
            images.map((image) => ({ key: image.key })),
          ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['posts', username] });

      /*
       * And again shortly, if there were pictures.
       *
       * The image rows are written by a worker, not by the request that created
       * the post - so the refetch above almost always lands before they exist,
       * and the update appears with its text and no pictures until something
       * else happens to refresh the list. Which, on a profile somebody is
       * looking at, is nothing.
       *
       * Two follow-ups rather than a poll: the job takes about a second, and a
       * pair of cheap refetches is less machinery than a polling loop that has
       * to know when to stop.
       */
      if (images.length > 0) {
        const again = () => queryClient.invalidateQueries({ queryKey: ['posts', username] });

        window.setTimeout(again, 1500);
        window.setTimeout(again, 5000);
      }

      setBody('');
      setVisibility('public');
      images.forEach((image) => URL.revokeObjectURL(image.preview));
      setImages([]);
      onDone?.();
    },
  });

  const pick = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploadFailed(false);
    setUploading(true);

    try {
      const room = MAX_IMAGES - images.length;
      const chosen = [...files].slice(0, room);

      const uploaded = await Promise.all(
        chosen.map(async (file) => ({
          key: await posts.uploadImage(file),
          // A local preview, so the picture is on screen before the round trip
          // finishes. Revoked when the composer is done with it.
          preview: URL.createObjectURL(file),
        })),
      );

      setImages((current) => [...current, ...uploaded]);
    } catch {
      setUploadFailed(true);
    } finally {
      setUploading(false);

      // Cleared, or picking the same file twice in a row fires no change event.
      if (picker.current) picker.current.value = '';
    }
  };

  const drop = (index: number) => {
    setImages((current) => {
      URL.revokeObjectURL(current[index]!.preview);

      return current.filter((_, i) => i !== index);
    });
  };

  const text = body.trim();
  const tooLong = body.length > MAX_BODY;
  const ready = text !== '' && !tooLong && !save.isPending && !uploading;

  return (
    <Card className="flex flex-col gap-3 p-4" data-post-composer>
      <Textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder="Studio notes, a release date, what you are working on…"
        aria-label={editing ? 'Edit your update' : 'Write an update'}
        data-post-body
        className="min-h-24 resize-y rounded-[var(--radius-control)] border border-line bg-app px-3 py-2.5 text-sm text-ink placeholder:text-faint focus:border-line-strong"
      />

      {images.length > 0 && (
        <ul className="flex flex-wrap gap-2" data-post-previews>
          {images.map((image, index) => (
            <li key={image.key} className="relative">
              <img
                src={image.preview}
                alt=""
                className="h-20 w-20 rounded-[var(--radius-control)] border border-line object-cover"
              />
              <button
                type="button"
                onClick={() => drop(index)}
                aria-label="Remove this image"
                data-post-image-remove
                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-[var(--radius-pill)] border border-line-strong bg-surface text-[11px] text-soft transition-colors hover:text-ink"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/*
          Radios rather than a select. Two options, and both of them are the
          kind somebody wants to see before choosing - a closed menu showing
          "Public" is a decision already made on their behalf.
        */}
        <div
          role="radiogroup"
          aria-label="Who can see this"
          data-post-visibility
          className="flex items-center gap-1 rounded-[var(--radius-pill)] border border-line p-1"
        >
          {(
            [
              { value: 'public' as const, label: 'Public' },
              { value: 'followers' as const, label: 'Followers' },
            ]
          ).map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={visibility === option.value}
              data-post-visibility-option={option.value}
              onClick={() => setVisibility(option.value)}
              className={`rounded-[var(--radius-pill)] px-3 py-1 text-[12px] font-medium transition-colors ${
                visibility === option.value
                  ? 'bg-accent text-accent-ink'
                  : 'text-soft hover:bg-raised hover:text-ink'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {/*
            Editing leaves the pictures alone.

            Changing which images are on a post means reconciling positions,
            objects and rows against what is already there - a bigger job than
            fixing a typo, and not one somebody expects from a Save button.
          */}
          {!editing && (
            <>
              <input
                ref={picker}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                data-post-image-input
                onChange={(event) => void pick(event.target.files)}
              />

              <Button
                variant="ghost"
                onClick={() => picker.current?.click()}
                disabled={uploading || images.length >= MAX_IMAGES}
                data-post-image-add
              >
                {uploading ? 'Adding…' : 'Add image'}
              </Button>
            </>
          )}

          {/* Only once it is close. See the note on the component. */}
          {body.length > MAX_BODY - 200 && (
            <span className={`font-mono text-[11px] ${tooLong ? 'text-hot-text' : 'text-faint'}`}>
              {MAX_BODY - body.length}
            </span>
          )}

          {editing && (
            <Button variant="ghost" onClick={onDone} disabled={save.isPending}>
              Cancel
            </Button>
          )}

          <Button loading={save.isPending} disabled={!ready} onClick={() => save.mutate()} data-post-save>
            {editing ? 'Save' : 'Post'}
          </Button>
        </div>
      </div>

      <p className="text-xs text-faint">
        {visibility === 'public'
          ? 'Anyone can see this, including people who are not signed in.'
          : 'Only people who follow you can see this.'}
      </p>

      {uploadFailed && (
        <Alert tone="error">That image could not be uploaded. Try a smaller one.</Alert>
      )}

      {save.isError && (
        <Alert tone="error">
          {save.error instanceof ApiError ? save.error.message : 'That could not be posted.'}
        </Alert>
      )}
    </Card>
  );
}
